/**
 * Dashboard roles and team rules, shared by the API (enforcement) and the SPA
 * (labels, nav, route guard, Team page copy).
 *
 * Pure on purpose: no Node or browser imports, so the SPA can import this file
 * the same way it imports reports/shared/engagement-scope.
 *
 * Stored values live in public.user_roles.role. 'generator' predates the Team
 * page and is shown as "Editor"; it keeps its stored name so every existing
 * requireRole('generator') gate keeps working unchanged.
 */

export const DASHBOARD_ROLES = ['admin', 'generator', 'pm', 'viewer'] as const;
export type DashboardRole = (typeof DASHBOARD_ROLES)[number];

/** A user with no user_roles row is treated as a viewer (matches loadDashboardAccess). */
export const DEFAULT_ROLE: DashboardRole = 'viewer';

export function isDashboardRole(value: unknown): value is DashboardRole {
  return typeof value === 'string' && (DASHBOARD_ROLES as readonly string[]).includes(value);
}

/** A stored user_roles.role value, or DEFAULT_ROLE when missing or unknown. */
export function normalizeRole(value: unknown): DashboardRole {
  return isDashboardRole(value) ? value : DEFAULT_ROLE;
}

export interface RoleInfo {
  label: string;
  summary: string;
  /** Short "can see" summary for the members table. */
  access: string;
}

export const ROLE_INFO: Record<DashboardRole, RoleInfo> = {
  admin: {
    label: 'Admin',
    summary: 'Everything, plus managing the team.',
    access: 'All areas + team',
  },
  generator: {
    label: 'Editor',
    summary: 'Runs the work in every area. Cannot manage the team.',
    access: 'All areas',
  },
  pm: {
    label: 'Project Manager',
    summary: 'SOWs only, to start. Create, edit and download.',
    access: 'SOWs only',
  },
  viewer: {
    label: 'Viewer',
    summary: 'Reads dashboards and reports. Cannot send outreach or write SOWs.',
    access: 'Most areas',
  },
};

/** Order roles appear in pickers. */
export const ROLE_PICKER_ORDER: DashboardRole[] = ['admin', 'generator', 'pm', 'viewer'];

/**
 * What each role can do, per area. Mirrors what the API and RLS enforce today,
 * so the Team page never promises more (or less) than the code allows.
 * `null` means no access.
 */
export interface AccessArea {
  key: 'finance' | 'health' | 'outreach' | 'chemistry' | 'sow' | 'affiliates' | 'team';
  label: string;
  detail: string;
  access: Record<DashboardRole, string | null>;
}

export const ACCESS_MATRIX: AccessArea[] = [
  {
    key: 'finance',
    label: 'Finance reports',
    detail: 'Dashboard, Reports, Sent to finance',
    access: { admin: 'Full', generator: 'Generate, record sends', pm: null, viewer: 'View, generate, record sends' },
  },
  {
    key: 'health',
    label: 'Engagement health',
    detail: 'Alerts and AM/CSM digests',
    access: { admin: 'Full', generator: 'Refresh, snooze, send digests', pm: null, viewer: 'View, refresh, snooze' },
  },
  {
    key: 'outreach',
    label: 'Facilitator outreach',
    detail: 'Outreach and Replies',
    access: { admin: 'Full', generator: 'Send, triage replies', pm: null, viewer: 'View drafts' },
  },
  {
    key: 'chemistry',
    label: 'Chemistry checks',
    detail: 'Cases and calendar',
    access: { admin: 'Full', generator: 'Run cases', pm: null, viewer: 'View' },
  },
  {
    key: 'sow',
    label: 'SOWs',
    detail: 'Drafts, versions, downloads',
    access: {
      admin: 'Full, including delete',
      generator: 'Create, edit, download',
      pm: 'Create, edit, download',
      viewer: 'View, download',
    },
  },
  {
    key: 'affiliates',
    label: 'Affiliates',
    detail: 'Affiliate roster',
    access: { admin: 'View', generator: 'View', pm: null, viewer: 'View' },
  },
  {
    key: 'team',
    label: 'Team',
    detail: 'Settings › Team',
    access: { admin: 'Invite, change roles, deactivate', generator: null, pm: null, viewer: null },
  },
];

// ---------------------------------------------------------------------------
// Project Manager scope
// ---------------------------------------------------------------------------

/** Where a Project Manager lands, and the only area they use. */
export const PM_HOME_ROUTE = '/sow';

/**
 * API paths (relative to /api/dashboard) a Project Manager may call. Everything
 * else is denied by default, so new areas stay closed to PMs until opened here.
 */
export function pmMayUseApi(path: string): boolean {
  return path === '/sow' || path.startsWith('/sow/');
}

/** SPA routes a Project Manager may open. */
export function pmMayOpenRoute(pathname: string): boolean {
  return (
    pathname === '/sow' ||
    pathname.startsWith('/sow/') ||
    pathname === '/settings' ||
    pathname === '/welcome'
  );
}

export function homeRouteFor(role: DashboardRole | null | undefined): string {
  return role === 'pm' ? PM_HOME_ROUTE : '/';
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export type MemberStatus = 'active' | 'invited' | 'deactivated';

/** auth user_metadata key /welcome stamps when the person sets their password. */
export const WELCOME_COMPLETED_KEY = 'welcome_completed_at';

export function deriveMemberStatus(input: {
  lastSignInAt: string | null;
  invitedAt: string | null;
  deactivatedAt: string | null;
  /**
   * Invited from Settings › Team but has not finished /welcome (set a
   * password) yet. Opening the link signs them in, so lastSignInAt alone
   * cannot tell an accepted invite from a half-finished one.
   */
  welcomePending?: boolean;
}): MemberStatus {
  if (input.deactivatedAt) return 'deactivated';
  if (input.welcomePending) return 'invited';
  if (!input.lastSignInAt && input.invitedAt) return 'invited';
  return 'active';
}

export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase();
}

/**
 * True when the address is outside the organization's own domains (for
 * example Kelm staff who support the dashboard). External admins do not count
 * toward the "at least one admin" rule.
 */
export function isExternalEmail(email: string, orgDomains: readonly string[]): boolean {
  if (orgDomains.length === 0) return false;
  return !orgDomains.includes(emailDomain(email));
}

/** Split a pasted list of addresses (commas, semicolons, spaces, new lines). */
export function parseEmailList(raw: string | readonly string[]): string[] {
  const parts = (Array.isArray(raw) ? raw.join(',') : String(raw))
    .split(/[\s,;]+/)
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(parts));
}

export type InviteEmailProblem = 'invalid' | 'domain';

export function checkInviteEmail(
  email: string,
  allowedDomains: readonly string[],
): InviteEmailProblem | null {
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return 'invalid';
  if (allowedDomains.length > 0 && !allowedDomains.includes(emailDomain(email))) return 'domain';
  return null;
}

export function inviteProblemMessage(
  email: string,
  problem: InviteEmailProblem,
  allowedDomains: readonly string[],
): string {
  if (problem === 'invalid') return `"${email}" is not an email address.`;
  const domains = allowedDomains.map((d) => `@${d}`).join(' or ');
  return `Only ${domains} addresses can be invited here. Ask Kelm to add anyone outside Ariel.`;
}

// ---------------------------------------------------------------------------
// Guards. Messages are shown to admins as-is.
// ---------------------------------------------------------------------------

export interface GuardMember {
  id: string;
  role: DashboardRole;
  status: MemberStatus;
  external: boolean;
}

export const LAST_ADMIN_MESSAGE = 'Ariel needs at least one admin. Make someone else an admin first.';

function isOnlyOrgAdmin(target: GuardMember, members: readonly GuardMember[]): boolean {
  if (target.role !== 'admin' || target.status !== 'active' || target.external) return false;
  const orgAdmins = members.filter((m) => m.role === 'admin' && m.status === 'active' && !m.external);
  return orgAdmins.length <= 1;
}

export function roleChangeProblem(args: {
  actorId: string;
  target: GuardMember;
  nextRole: DashboardRole;
  members: readonly GuardMember[];
}): string | null {
  const { actorId, target, nextRole, members } = args;
  if (target.id === actorId) return 'You cannot change your own role. Ask another admin.';
  if (target.status === 'deactivated') return 'Restore their access before changing their role.';
  if (target.role === nextRole) return 'They already have that role.';
  if (nextRole !== 'admin' && isOnlyOrgAdmin(target, members)) return LAST_ADMIN_MESSAGE;
  return null;
}

export function deactivateProblem(args: {
  actorId: string;
  target: GuardMember;
  members: readonly GuardMember[];
}): string | null {
  const { actorId, target, members } = args;
  if (target.id === actorId) return 'You cannot turn off your own access.';
  if (target.status === 'deactivated') return 'Their access is already off.';
  if (target.status === 'invited') return 'They have not accepted yet. Revoke the invite instead.';
  if (isOnlyOrgAdmin(target, members)) return LAST_ADMIN_MESSAGE;
  return null;
}

export function revokeProblem(target: GuardMember): string | null {
  return target.status === 'invited' ? null : 'Only pending invites can be revoked.';
}

export function restoreProblem(target: GuardMember): string | null {
  return target.status === 'deactivated' ? null : 'Their access is already on.';
}

export function resendProblem(target: GuardMember): string | null {
  return target.status === 'invited' ? null : 'They have already accepted their invite.';
}

// ---------------------------------------------------------------------------
// Activity log wording
// ---------------------------------------------------------------------------

export type TeamAction =
  | 'invited'
  | 'invite_resent'
  | 'role_changed'
  | 'deactivated'
  | 'restored'
  | 'invite_revoked';

export interface TeamActivityEntry {
  id: string;
  at: string;
  actorName: string;
  action: TeamAction;
  targetEmail: string;
  targetName: string | null;
  details: { role?: string; fromRole?: string; toRole?: string };
}

function roleLabel(value: string | undefined): string {
  return value && isDashboardRole(value) ? ROLE_INFO[value].label : 'a new role';
}

export function describeActivity(entry: TeamActivityEntry): string {
  const who = entry.targetName || entry.targetEmail;
  switch (entry.action) {
    case 'invited':
      return `${entry.actorName} invited ${entry.targetEmail} as ${roleLabel(entry.details.role)}`;
    case 'invite_resent':
      return `${entry.actorName} resent the invite to ${entry.targetEmail}`;
    case 'role_changed':
      return `${entry.actorName} changed ${who} from ${roleLabel(entry.details.fromRole)} to ${roleLabel(entry.details.toRole)}`;
    case 'deactivated':
      return `${entry.actorName} turned off access for ${who}`;
    case 'restored':
      return `${entry.actorName} restored access for ${who}`;
    case 'invite_revoked':
      return `${entry.actorName} revoked the invite to ${entry.targetEmail}`;
    default:
      return `${entry.actorName} updated ${who}`;
  }
}
